import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useShopRole } from "@/hooks/useShopRole";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, Circle, Trash2, Plus, Pencil } from "lucide-react";
import { EditShopItemDialog } from "@/components/EditShopItemDialog";
import { Switch } from "@/components/ui/switch";
import { ImageUploader } from "@/components/ImageUploader";

interface Task {
  id: string;
  title: string;
  description: string | null;
  task_type: 'personal' | 'assigned' | 'general';
  assigned_to: string | null;
  created_by: string | null;
  status: 'pending' | 'completed';
  completed_at: string | null;
  created_at: string;
}

export default function ShopDashboard() {
  const { user } = useAuth();
  const { hasShopAccess } = useShopRole();
  const { toast } = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [ptaId, setPtaId] = useState<string | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [pendingItems, setPendingItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [newItemImages, setNewItemImages] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      fetchPtaId();
    }
  }, [user]);

  useEffect(() => {
    if (ptaId) {
      fetchTasks();
    }
  }, [ptaId]);

  useEffect(() => {
    if (ptaId) {
      fetchItems();
      fetchPendingItems();
      fetchCategories();
      fetchSettings();
    }
  }, [ptaId]);

  const fetchCategories = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase.from('shop_categories').select('*').eq('pta_id', ptaId).order('display_order');
    if (error) {
      console.error('Error fetching categories:', error);
      toast({ title: "Error loading categories", description: error.message, variant: "destructive" });
      return;
    }
    
    // Count items per category
    const categoriesWithCounts = await Promise.all(
      (data || []).map(async (cat) => {
        const { count } = await supabase
          .from('shop_items')
          .select('*', { count: 'exact', head: true })
          .eq('pta_id', ptaId)
          .eq('category', cat.name)
          .eq('status', 'active');
        return { ...cat, item_count: count || 0 };
      })
    );
    
    setCategories(categoriesWithCounts);
  };

  const fetchSettings = async () => {
    if (!ptaId) return;
    const { data } = await supabase.from('shop_settings').select('*').eq('pta_id', ptaId).single();
    if (!data) {
      const { data: newSettings, error: settingsError } = await supabase
        .from('shop_settings')
        .insert({ pta_id: ptaId })
        .select()
        .single();
      
      if (settingsError) {
        console.error('Error creating settings:', settingsError);
        toast({ title: "Error", description: "Failed to create shop settings", variant: "destructive" });
        return null;
      }
      
      setSettings(newSettings);
      
      // Check if categories already exist
      const { data: existingCategories } = await supabase
        .from('shop_categories')
        .select('id')
        .eq('pta_id', ptaId);
      
      // Create default categories only if none exist
      if (!existingCategories || existingCategories.length === 0) {
        const defaultCategories = ['Uniforms', 'Books', 'Equipment', 'Other'];
        const results = await Promise.allSettled(
          defaultCategories.map((name, idx) =>
            supabase.from('shop_categories').insert({ 
              pta_id: ptaId, 
              name, 
              display_order: idx 
            })
          )
        );
        
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          console.error('Some categories failed to create:', failed);
        }
      }
      
      fetchCategories();
      return newSettings;
    }
    setSettings(data);
    return data;
  };

  const ensureSettings = async () => {
    if (settings?.id) return settings;
    return await fetchSettings();
  };

  const fetchPtaId = async () => {
    const { data } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .single();
    
    if (data) setPtaId(data.pta_id);
  };

  const fetchItems = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from('shop_items')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('status', 'active')
      .order('created_at', { ascending: false });
    setItems(data || []);
  };

  const fetchPendingItems = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from('shop_items')
      .select(`*, seller_parent:parents!seller_parent_id(full_name)`)
      .eq('pta_id', ptaId)
      .eq('seller_type', 'parent')
      .eq('approval_status', 'pending')
      .order('created_at', { ascending: false});
    setPendingItems(data || []);
  };

  const handleApproveItem = async (itemId: string) => {
    const { error } = await supabase
      .from('shop_items')
      .update({ 
        approval_status: 'approved',
        approved_by: user?.id,
        approved_at: new Date().toISOString()
      })
      .eq('id', itemId);

    if (!error) {
      toast({ title: "Item approved successfully" });
      fetchPendingItems();
      fetchItems();
    }
  };

  const handleRejectItem = async (itemId: string, reason: string) => {
    const { error } = await supabase
      .from('shop_items')
      .update({ 
        approval_status: 'rejected',
        rejection_reason: reason,
        approved_by: user?.id,
        approved_at: new Date().toISOString()
      })
      .eq('id', itemId);

    if (!error) {
      toast({ title: "Item rejected" });
      fetchPendingItems();
    }
  };

  const fetchTasks = async () => {
    if (!user || !ptaId) return;

    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('assigned_to', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) setTasks(data as Task[]);
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !ptaId) return;

    const { error } = await supabase
      .from('tasks')
      .insert([{
        title: newTaskTitle,
        description: newTaskDescription || null,
        task_type: 'personal',
        assigned_to: user.id,
        created_by: user.id,
        status: 'pending',
        pta_id: ptaId
      }]);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: "Task added", description: "Your task has been created successfully." });
      setNewTaskTitle("");
      setNewTaskDescription("");
      setIsTaskDialogOpen(false);
      fetchTasks();
    }
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    const { error } = await supabase
      .from('tasks')
      .update({ 
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null
      })
      .eq('id', taskId);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      fetchTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Task deleted", description: "The task has been removed." });
      fetchTasks();
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Shop Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Manage your marketplace items and settings
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="inventory">Inventory</TabsTrigger>
          <TabsTrigger value="pending">Pending ({pendingItems.length})</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="tasks">Tasks</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Listings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{items.length}</div>
                <p className="text-xs text-muted-foreground">Total active items</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Approval</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{pendingItems.length}</div>
                <p className="text-xs text-muted-foreground">Awaiting review</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="inventory" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>PTA Items</CardTitle>
                  <CardDescription>Manage items sold by your PTA</CardDescription>
                </div>
                <Button onClick={() => setShowAddItemDialog(true)}><Plus className="h-4 w-4 mr-2" />Add Item</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {items.filter(i => i.seller_type === 'pta').map(item => (
                  <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  {item.images?.[0] && <img src={item.images[0]} alt={item.name} className="w-16 h-16 object-cover rounded" />}
                  <div>
                    <h4 className="font-semibold">{item.name}</h4>
                    <p className="text-sm text-muted-foreground">{item.category}</p>
                    {item.price && <p className="text-sm font-semibold text-primary">£{parseFloat(item.price).toFixed(2)}</p>}
                  </div>
                </div>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedItem(item); setShowEditDialog(true); }}>Edit</Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Pending Parent Submissions</CardTitle>
              <CardDescription>Review and approve parent listings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pendingItems.map(item => (
                  <div key={item.id} className="p-4 border rounded-lg space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4">
                        {item.images?.[0] ? (
                          <img src={item.images[0]} alt={item.name} className="w-20 h-20 object-cover rounded" />
                        ) : (
                          <div className="w-20 h-20 bg-muted rounded flex items-center justify-center">📦</div>
                        )}
                        <div>
                          <h4 className="font-semibold">{item.name}</h4>
                          <p className="text-sm text-muted-foreground">By: {item.seller_parent?.full_name}</p>
                          <p className="text-sm text-muted-foreground">{item.category}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleApproveItem(item.id)}>
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => {
                            const reason = prompt("Rejection reason:");
                            if (reason) handleRejectItem(item.id, reason);
                          }}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm">{item.description}</p>
                  </div>
                ))}
                {pendingItems.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No pending submissions</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Manage Categories</CardTitle>
                  <CardDescription>Add and organize marketplace categories</CardDescription>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button><Plus className="h-4 w-4 mr-2" />Add Category</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Category</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      const formData = new FormData(e.currentTarget);
                      const { error } = await supabase.from('shop_categories').insert({
                        pta_id: ptaId,
                        name: formData.get('name') as string,
                        display_order: categories.length
                      });
                      if (error) {
                        toast({ title: "Error", description: error.message, variant: "destructive" });
                      } else {
                        toast({ title: "Category added" });
                        fetchCategories();
                        (e.target as HTMLFormElement).reset();
                      }
                    }} className="space-y-4">
                      <div>
                        <Label>Category Name</Label>
                        <Input name="name" required />
                      </div>
                      <Button type="submit" className="w-full">Add Category</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {categories.map((cat, idx) => (
                  <div key={cat.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{cat.name}</span>
                      <Badge variant="outline">{cat.item_count || 0} items</Badge>
                    </div>
                    <div className="flex items-center gap-1">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Edit Category</DialogTitle>
                          </DialogHeader>
                          <form onSubmit={async (e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target as HTMLFormElement);
                            const newName = formData.get('name') as string;
                            const { error } = await supabase
                              .from('shop_categories')
                              .update({ name: newName })
                              .eq('id', cat.id);
                            if (error) {
                              toast({ title: "Error", description: error.message, variant: "destructive" });
                            } else {
                              toast({ title: "Category updated" });
                              fetchCategories();
                            }
                          }} className="space-y-4">
                            <div>
                              <Label>Category Name</Label>
                              <Input name="name" defaultValue={cat.name} required />
                            </div>
                            <DialogClose asChild>
                              <Button type="submit" className="w-full">Save Changes</Button>
                            </DialogClose>
                          </form>
                        </DialogContent>
                      </Dialog>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={async () => {
                          if (confirm(`Delete "${cat.name}" category?`)) {
                            await supabase.from('shop_categories').delete().eq('id', cat.id);
                            fetchCategories();
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Payment Methods</CardTitle><CardDescription>Configure how parents can pay for PTA items</CardDescription></CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Stripe Payments</Label>
                  <p className="text-sm text-muted-foreground">Accept online card payments (requires Stripe account)</p>
                </div>
                <Switch checked={settings?.stripe_enabled || false} onCheckedChange={async (checked) => {
                  const currentSettings = await ensureSettings();
                  if (!currentSettings?.id) return;
                  await supabase.from('shop_settings').update({ stripe_enabled: checked }).eq('id', currentSettings.id);
                  fetchSettings();
                }} />
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>BACS Transfer</Label>
                    <p className="text-sm text-muted-foreground">Bank transfer payments</p>
                  </div>
                  <Switch checked={settings?.bacs_enabled || false} onCheckedChange={async (checked) => {
                    const currentSettings = await ensureSettings();
                    if (!currentSettings?.id) return;
                    await supabase.from('shop_settings').update({ bacs_enabled: checked }).eq('id', currentSettings.id);
                    fetchSettings();
                  }} />
                </div>
                {settings?.bacs_enabled && (
                  <div className="pl-4 space-y-2 border-l-2">
                    <div><Label>Account Number</Label><Input value={settings?.bacs_account_number || ''} onChange={async (e) => {
                      await supabase.from('shop_settings').update({ bacs_account_number: e.target.value }).eq('id', settings.id);
                    }} /></div>
                    <div><Label>Sort Code</Label><Input value={settings?.bacs_sort_code || ''} onChange={async (e) => {
                      await supabase.from('shop_settings').update({ bacs_sort_code: e.target.value }).eq('id', settings.id);
                    }} /></div>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Cash Payments</Label>
                    <p className="text-sm text-muted-foreground">Accept cash payments</p>
                  </div>
                  <Switch checked={settings?.cash_enabled || false} onCheckedChange={async (checked) => {
                    const currentSettings = await ensureSettings();
                    if (!currentSettings?.id) return;
                    await supabase.from('shop_settings').update({ cash_enabled: checked }).eq('id', currentSettings.id);
                    fetchSettings();
                  }} />
                </div>
                {settings?.cash_enabled && (
                  <div className="pl-4 border-l-2">
                    <Label>Collection Instructions</Label>
                    <Textarea value={settings?.cash_instructions || ''} onChange={async (e) => {
                      const value = e.target.value;
                      setSettings((prev: any) => ({ ...prev, cash_instructions: value }));
                      await supabase.from('shop_settings').update({ cash_instructions: value }).eq('id', settings.id);
                    }} placeholder="e.g., Pay at school office during 9am-3pm" />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Cheque Payments</Label>
                    <p className="text-sm text-muted-foreground">Accept cheque payments</p>
                  </div>
                  <Switch checked={settings?.cheque_enabled || false} onCheckedChange={async (checked) => {
                    const currentSettings = await ensureSettings();
                    if (!currentSettings?.id) return;
                    await supabase.from('shop_settings').update({ cheque_enabled: checked }).eq('id', currentSettings.id);
                    fetchSettings();
                  }} />
                </div>
                {settings?.cheque_enabled && (
                  <div className="pl-4 space-y-2 border-l-2">
                    <div><Label>Payable To</Label><Input value={settings?.cheque_payable_to || ''} onChange={async (e) => {
                      const value = e.target.value;
                      setSettings((prev: any) => ({ ...prev, cheque_payable_to: value }));
                      await supabase.from('shop_settings').update({ cheque_payable_to: value }).eq('id', settings.id);
                    }} placeholder="PTA Name" /></div>
                    <div><Label>Mailing Address</Label><Textarea value={settings?.cheque_address || ''} onChange={async (e) => {
                      const value = e.target.value;
                      setSettings((prev: any) => ({ ...prev, cheque_address: value }));
                      await supabase.from('shop_settings').update({ cheque_address: value }).eq('id', settings.id);
                    }} placeholder="School address" /></div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Parent Selling</CardTitle><CardDescription>Allow parents to list items for sale</CardDescription></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Parent Selling</Label>
                  <p className="text-sm text-muted-foreground">Allow parents to create their own listings</p>
                </div>
                <Switch checked={settings?.parent_selling_enabled || false} onCheckedChange={async (checked) => {
                  const currentSettings = await ensureSettings();
                  if (!currentSettings?.id) return;
                  await supabase.from('shop_settings').update({ parent_selling_enabled: checked }).eq('id', currentSettings.id);
                  fetchSettings();
                }} />
              </div>

              {settings?.parent_selling_enabled && (
                <div className="space-y-4 pl-4 border-l-2">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Require Approval</Label>
                      <p className="text-sm text-muted-foreground">Parent posts need approval before going live</p>
                    </div>
                    <Switch checked={settings?.parent_post_approval_required || false} onCheckedChange={async (checked) => {
                      const currentSettings = await ensureSettings();
                      if (!currentSettings?.id) return;
                      await supabase.from('shop_settings').update({ parent_post_approval_required: checked }).eq('id', currentSettings.id);
                      fetchSettings();
                    }} />
                  </div>

                  <div>
                    <Label>Credits Per Post</Label>
                    <Input type="number" min="1" value={settings?.credit_cost_per_post || 1} onChange={async (e) => {
                      const value = parseInt(e.target.value);
                      setSettings((prev: any) => ({ ...prev, credit_cost_per_post: value }));
                      await supabase.from('shop_settings').update({ credit_cost_per_post: value }).eq('id', settings.id);
                    }} />
                    <p className="text-xs text-muted-foreground mt-1">How many credits parents spend to create a listing</p>
                  </div>

                  <div>
                    <Label>Post Expiry (Days)</Label>
                    <Input type="number" min="0" placeholder="Leave empty for no expiry" value={settings?.post_expiry_days || ''} onChange={async (e) => {
                      const value = e.target.value ? parseInt(e.target.value) : null;
                      setSettings((prev: any) => ({ ...prev, post_expiry_days: value }));
                      await supabase.from('shop_settings').update({ post_expiry_days: value }).eq('id', settings.id);
                    }} />
                    <p className="text-xs text-muted-foreground mt-1">Listings auto-expire after this many days</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle>Terms of Use</CardTitle><CardDescription>Terms parents must accept when listing items</CardDescription></CardHeader>
            <CardContent>
              <Textarea 
                rows={10} 
                value={settings?.terms_of_use || `By listing items for sale, you agree to the following terms:

1. Prohibited Items: You may not sell weapons, alcohol, tobacco, illegal substances, or any items that violate local laws.

2. Accurate Descriptions: You must provide accurate and honest descriptions of items. Misleading information is not permitted.

3. Direct Payment: All payments between parents are handled directly between buyer and seller. The PTA is not responsible for payment processing or disputes.

4. PTA Liability: The PTA accepts no responsibility for transactions between parents, item quality, delivery, or disputes. All sales are final between buyer and seller.

5. Communication: Be respectful and responsive when communicating with potential buyers. Provide accurate contact information.

6. Compliance: You are responsible for complying with all applicable laws and regulations when selling items.

The PTA reserves the right to remove any listing that violates these terms or is deemed inappropriate.`} 
                onChange={async (e) => {
                  const value = e.target.value;
                  setSettings((prev: any) => ({ ...prev, terms_of_use: value }));
                  await supabase.from('shop_settings').update({ terms_of_use: value }).eq('id', settings.id);
                }} 
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tasks" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>My Tasks</CardTitle>
                  <CardDescription>Manage your personal tasks and view assigned tasks</CardDescription>
                </div>
                <Dialog open={isTaskDialogOpen} onOpenChange={setIsTaskDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Task
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Task</DialogTitle>
                      <DialogDescription>Add a personal task to your list</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddTask} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="task-title">Task Title *</Label>
                        <Input
                          id="task-title"
                          value={newTaskTitle}
                          onChange={(e) => setNewTaskTitle(e.target.value)}
                          placeholder="Enter task title"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="task-description">Description</Label>
                        <Textarea
                          id="task-description"
                          value={newTaskDescription}
                          onChange={(e) => setNewTaskDescription(e.target.value)}
                          placeholder="Enter task description (optional)"
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <DialogClose asChild>
                          <Button type="button" variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button type="submit">Add Task</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No tasks yet. Add a task to get started!
                </p>
              ) : (
                <div className="space-y-3">
                  {tasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-start gap-3 p-3 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <button
                        onClick={() => handleToggleTask(task.id, task.status)}
                        className="mt-1"
                      >
                        {task.status === 'completed' ? (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground" />
                        )}
                      </button>
                      <div className="flex-1">
                        <h4 className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                          {task.title}
                        </h4>
                        {task.description && (
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant={task.task_type === 'personal' ? 'default' : 'secondary'}>
                            {task.task_type}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(task.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      {task.task_type === 'personal' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTask(task.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {selectedItem && showEditDialog && (
        <EditShopItemDialog item={selectedItem} open={showEditDialog} onOpenChange={setShowEditDialog} onSuccess={fetchItems} categories={categories} />
      )}

      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add PTA Item</DialogTitle>
            <DialogDescription>Create a new item for your PTA marketplace</DialogDescription>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            const price = formData.get('price') as string;
            const { error } = await supabase.from('shop_items').insert({
              pta_id: ptaId,
              seller_type: 'pta',
              approval_status: 'approved',
              name: formData.get('name') as string,
              description: formData.get('description') as string,
              category: formData.get('category') as string,
              contact_method: formData.get('contact_method') as string,
              price: price ? parseFloat(price) : 0,
              images: newItemImages,
              status: 'active'
            } as any);
            if (error) {
              toast({ title: "Error", description: error.message, variant: "destructive" });
            } else {
              toast({ title: "Item added successfully" });
              setShowAddItemDialog(false);
              setNewItemImages([]);
              fetchItems();
            }
          }} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" name="name" required maxLength={100} />
            </div>
            <div>
              <Label htmlFor="price">Price (£)</Label>
              <Input id="price" name="price" type="number" step="0.01" min="0" placeholder="Optional" />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" name="description" rows={4} />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <select id="category" name="category" className="w-full rounded-md border border-input bg-background px-3 py-2" required>
                {categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Contact Method</Label>
              <Input id="contact_method" name="contact_method" placeholder="e.g., Email: pta@school.com or Phone: 01234567890" />
            </div>
            <div>
              <Label>Images (up to 3)</Label>
              <ImageUploader
                ptaId={ptaId || ''}
                itemId="new"
                images={newItemImages}
                onChange={setNewItemImages}
                maxImages={3}
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddItemDialog(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1">Add Item</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
